import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks';
import { IconButton, Section, SectionContent, SectionHeader, SectionHeaderLabel } from '../Styles';
import { Icon } from '../../../shared';
import AuctionLaneContent from '../AuctionLanes/AuctionLaneContent';
import LaneContentDisconnected from '../AuctionLanes/LaneContentDisconnected';

export const Auctions = observer(() => {
    const {
        MultiAuctionStore: { user, allAuctionsAndLanes, auctions, addAuction, getAuction, registerBidder },
        providerStore,
        DataStore: { addNewConnection },
        UIStore: { handleJoinLane }
    } = useStores();
    const [openSections, setOpenSections] = useState([]);
    const [openSection, setOpenSection] = useState(null);
    const [initialSectionOpened, setInitialSectionOpened] = useState(false);
    const [loadingLane, setLoadingLane] = useState(null);

    const toggleLaneSection = (auctionID) => {
        setOpenSections((prevOpen) =>
            prevOpen.includes(auctionID)
                ? prevOpen.filter((id) => id !== auctionID) // Close section if already open
                : [...prevOpen, auctionID] // Open section if not already open
        );
    };

    const getToggleIcon = (isOpen) => (isOpen ? 'chevron-up' : 'chevron-down');

    const variants = {
        open: {
            opacity: 1,
            height: 'auto',
            transitionEnd: { overflow: 'visible', padding: 0 },
        },
        closed: {
            opacity: 0,
            height: 0,
            overflow: 'hidden',
            padding: 0,
        },
    };

    const auctionEntries = Array.from(auctions?.entries() ?? []);

    useEffect(() => {
        if (!initialSectionOpened) {
            // Find the first auctionStore where 'connected' is true
            const connectedAuction = auctionEntries.find(([, auctionStore]) => auctionStore.connected);

            if (connectedAuction) {
                const { auctionInfo } = connectedAuction[1]; // Extract auctionStore (index 1)
                const auctionName = auctionInfo?.auction; // Get the 'auction' value safely
                if (auctionName) {
                    setOpenSections((prev) => [...prev, auctionName]); // Add the auction name to open sections
                    setInitialSectionOpened(true);
                }
            }
        }
    }, [auctionEntries, initialSectionOpened, auctions]);

    const join = async (auctionId, laneId) => {
        setLoadingLane(laneId);
        try {
            const exists = getAuction(auctionId);

            if (exists) {
                handleJoinLane(auctionId, laneId);
                return;
            }

            const token = await registerBidder({sessionToken: user.sessionToken, auctionID: auctionId, laneID: laneId});
            if (!token) {
                return;
            }

            addAuction({key: auctionId, auctionID: auctionId, providerStore});
            await addNewConnection({key: auctionId, token, auctionID: auctionId, laneID: laneId});
        }
        catch (error) {
            alert(`There was a problem joining this lane: ${error.message}`)
            console.log(error);
        }
        finally {
            setLoadingLane(null);
        }
    };

    return (
        <Section>
            {allAuctionsAndLanes.map((auction) => {
                const isOpen = openSections.includes(auction.auction);

                // Check if the auction store exists and retrieve its color
                const auctionStore = auctions.get(auction.auctionID);
                const iconColor = auctionStore?.color || 'white'; // Default to white if no color is assigned

                return (
                    <Section key={auction.auctionID}>
                        <SectionHeader onClick={() => toggleLaneSection(auction.auction)}>
                            <SectionHeaderLabel>
                                <Icon
                                    icon="road"
                                    size={1}
                                    hoverPointer={false}
                                    style={{ color: iconColor }} // Dynamically set the icon color
                                />
                                {auction.auction}
                            </SectionHeaderLabel>
                            <IconButton>
                                <Icon
                                    icon={getToggleIcon(isOpen)}
                                    iconOnly
                                    size={0.8}
                                />
                            </IconButton>
                        </SectionHeader>
                        <AnimatePresence initial={false}>
                            {isOpen && (
                                <SectionContent
                                    variants={variants}
                                    initial="closed"
                                    animate="open"
                                    exit="closed"
                                >
                                    {auctionEntries?.some(([key, store]) => key === auction.auctionID || store.AuctionID === auction.auctionID) ? (
                                        <AuctionLaneContent
                                            auctionId={auctionEntries?.find(([key, store]) => key === auction.auctionID || store.AuctionID === auction.auctionID)?.[0]} // Pass the matching key
                                        />
                                    ) : (
                                        auction.lanes
                                            .sort((a, b) => a.lane.localeCompare(b.lane))
                                            .map((lane, i) =>
                                                <LaneContentDisconnected
                                                    item={lane}
                                                    key={i}
                                                    loading={loadingLane === lane.laneID}
                                                    onClick={() => join(auction.auctionID, lane.laneID)}
                                                />
                                            )
                                    )}

                                </SectionContent>
                            )}
                        </AnimatePresence>
                    </Section>
                );
            })}
        </Section>
    );
});

export default Auctions;
